import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabaseClient } from "../../lib/supabase";
import { toast } from "../../lib/toast";

export function UserCreate() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [password, setPassword] = useState(() => {
    // Generate a random password
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    return Array.from(crypto.getRandomValues(new Uint8Array(12)))
      .map(byte => chars[byte % chars.length])
      .join('');
  });

  useEffect(() => {
    async function checkUserRole() {
      try {
        const { data: { user }, error: authError } = await supabaseClient.auth.getUser();
        if (authError) {
          console.error('Auth error:', authError);
          navigate('/login');
          return;
        }
        
        if (!user) {
          console.log('No user found, redirecting to login');
          navigate('/login');
          return;
        }

        const { data: profile, error: profileError } = await supabaseClient
          .from('profiles')
          .select('role, email')
          .eq('id', user.id)
          .single();

        if (profileError) {
          console.error('Profile fetch error:', profileError);
          navigate('/');
          return;
        }

        console.log('User profile:', profile);

        if (!profile || (profile.role !== 'admin' && profile.email !== 'andmaillev@gmail.com')) {
          console.log('User not authorized:', profile);
          navigate('/');
          toast.error('Only administrators can create new users');
          return;
        }

        setUserRole(profile.role);
      } catch (err) {
        console.error('Error in checkUserRole:', err);
        navigate('/');
      }
    }

    checkUserRole();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (userRole !== 'admin') {
      console.error('Non-admin user attempted to create user');
      setError('Only administrators can create new users');
      return;
    }

    setLoading(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const full_name = formData.get("full_name") as string;
    const role = formData.get("role") as "admin" | "optimizer" | "client";
    
    console.log('Creating user with data:', {
      email,
      full_name,
      role,
      metadata: {
        role,
        full_name,
        requirePasswordChange: true
      }
    });

    try {
      // Create user with Supabase auth
      const { data, error: signUpError } = await supabaseClient.auth.signUp({
        email,
        password,
        options: {
          data: {
            role,
            full_name,
            requirePasswordChange: true
          }
        }
      });

      if (signUpError) {
        console.error('Signup error:', signUpError);
        throw signUpError;
      }

      if (!data.user) {
        console.error('No user returned from signup');
        throw new Error("Failed to create user");
      }

      console.log('User created successfully:', {
        id: data.user.id,
        email: data.user.email,
        metadata: data.user.user_metadata
      });

      // Check if profile was created by trigger
      const { data: profile, error: profileCheckError } = await supabaseClient
        .from('profiles')
        .select('*')
        .eq('id', data.user.id)
        .single();

      if (profileCheckError) {
        console.error('Error checking profile:', profileCheckError);
      } else {
        console.log('Profile created:', profile);
      }

      // Show success message with password
      toast.success(
        <div>
          <p>User created successfully!</p>
          <p className="mt-2 p-2 bg-gray-100 rounded font-mono text-sm">
            Initial password: {password}
          </p>
          <p className="mt-2 text-sm">
            Make sure to securely share this password with the user.
          </p>
        </div>,
        { duration: 10000 } // Show for 10 seconds
      );

      // Navigate back to the list
      navigate("/users");
    } catch (err) {
      console.error("Error creating user:", err);
      
      // Try to get more details about the error
      if (err instanceof Error) {
        console.error('Error details:', {
          name: err.name,
          message: err.message,
          stack: err.stack
        });
      }

      setError(err instanceof Error ? err.message : "Failed to create user");
      
      // Show detailed error in toast
      toast.error(
        <div>
          <p>Failed to create user</p>
          <p className="mt-2 text-sm text-red-600">
            {err instanceof Error ? err.message : "Unknown error occurred"}
          </p>
        </div>
      );
    } finally {
      setLoading(false);
    }
  };

  // If user role is not yet determined, show loading state
  if (!userRole) {
    return <div className="p-6">Loading...</div>;
  }

  // If user is not admin, this will be caught by useEffect and redirected
  if (userRole !== 'admin') {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-6">Create User</h2>
        
        {error && (
          <div className="mb-4 p-4 text-sm text-red-700 bg-red-100 rounded-md">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <input
              type="email"
              name="email"
              id="email"
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Initial Password
            </label>
            <div className="mt-1 flex rounded-md shadow-sm">
              <input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-l-md border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
              <button
                type="button"
                onClick={() => {
                  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
                  const newPassword = Array.from(crypto.getRandomValues(new Uint8Array(12)))
                    .map(byte => chars[byte % chars.length])
                    .join('');
                  setPassword(newPassword);
                }}
                className="relative -ml-px inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              >
                Generate
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Password must be at least 8 characters long. The user will be required to change it on first login.
            </p>
          </div>

          <div>
            <label htmlFor="full_name" className="block text-sm font-medium text-gray-700">
              Full Name
            </label>
            <input
              type="text"
              name="full_name"
              id="full_name"
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700">
              Role
            </label>
            <select
              name="role"
              id="role"
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="client">Client</option>
              <option value="optimizer">Optimizer</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate("/users")}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? "Creating..." : "Create User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}